<div class="flex flex-1 flex-col" role="search">
  <!-- ── Input ──────────────────────────────────────────────────────── -->
  <label class="flex h-14 shrink-0 items-center gap-3 border-b border-base-200 px-5">
    <ui-icon name="search" [size]="18" class="shrink-0 text-base-content/40"></ui-icon>
    <input
      #searchInput
      type="text"
      class="search-input grow bg-transparent text-base placeholder:text-base-content/40"
      placeholder="Search campaigns, fundraisers, teams and charities"
      autocomplete="off"
      [value]="query()"
      (input)="onSearch($any($event.target).value)"
      (keydown)="onKeydown($event)"
    />
    @if (query()) {
      <ui-button
        variant="ghost"
        shape="circle"
        size="xs"
        ariaLabel="Clear search"
        (mousedown)="$event.preventDefault()"
        (buttonClick)="clearSearch()"
      >
        <ui-icon name="x" [size]="14"></ui-icon>
      </ui-button>
    }
  </label>

  <!-- ── Results ────────────────────────────────────────────────────── -->
  <div
    id="gb-search-results"
    role="listbox"
    class="flex-1 max-h-[min(30rem,68vh)] overflow-y-auto overscroll-contain p-2"
  >
    @if (searching()) {
      <!-- Row skeletons with varied widths -->
      @for (skeletonWidth of skeletonRowWidths; track $index) {
        <div class="flex items-center gap-3 px-3 py-2.5">
          <div class="skeleton size-10 shrink-0 rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="skeleton h-3.5 rounded" [style.width]="skeletonWidth"></div>
            <div class="skeleton h-2.5 w-16 rounded"></div>
          </div>
        </div>
      }
    } @else if (hasError()) {
      <div class="flex flex-col items-center gap-2 px-4 py-12 text-center">
        <ui-icon name="triangle-alert" [size]="22" class="text-base-content/30"></ui-icon>
        <p class="text-sm font-semibold text-base-content/70">Couldn't load results</p>
        <ui-button
          variant="ghost"
          size="xs"
          extraClass="h-auto min-h-0 px-2 text-xs font-semibold normal-case text-primary"
          (mousedown)="$event.preventDefault(); retrySearch()"
        >
          Try again
        </ui-button>
      </div>
    } @else if (!query()) {
      @if (recent().length) {
        <p class="section-label text-base-content/40">Recent</p>
        <div class="flex flex-wrap gap-1.5 px-3 pb-2">
          @for (term of recent(); track term) {
            <ui-button
              variant="ghost"
              size="xs"
              extraClass="h-auto min-h-0 gap-1.5 rounded-full border border-base-300 px-3 py-1.5 text-xs font-medium normal-case"
              (mousedown)="$event.preventDefault(); searchAgain(term)"
            >
              <ui-icon name="clock" [size]="12" class="text-base-content/40"></ui-icon>
              {{ term }}
            </ui-button>
          }
        </div>
      }

      <!-- ── Popular charities ──────────────────────────────────────────── -->
      <p class="section-label text-base-content/40 mt-2">Popular charities</p>
      @if (charitiesLoading()) {
        <div class="grid grid-cols-3 gap-2 px-1 pb-2 sm:grid-cols-5">
          @for (row of [1, 2, 3, 4, 5]; track row) {
            <div class="flex flex-col items-center gap-1.5 p-2">
              <div class="skeleton size-10 shrink-0 rounded-full sm:size-14"></div>
              <div class="skeleton h-3 w-3/4"></div>
            </div>
          }
        </div>
      } @else if (popularCharityItems().length) {
        <div class="grid grid-cols-3 gap-2 px-1 pb-2 sm:grid-cols-5">
          @for (item of popularCharityItems(); track item.slug) {
            <button
              type="button"
              class="flex flex-col items-center gap-1.5 rounded-2xl p-2 transition-colors bg-base-200/50 hover:[background:var(--border-color-contrast)] sm:gap-2 sm:p-3 cursor-pointer"
              (click)="goTo('/' + item.slug)"
            >
              <ui-avatar
                [src]="item.thumbnail"
                [alt]="item.name"
                [name]="item.name"
                size="md"
                [border]="true"
              />
              <span
                class="max-w-full truncate text-center text-xs font-semibold leading-snug text-base-content sm:text-sm"
              >
                {{ item.name }}
              </span>
            </button>
          }
        </div>
      }

      <!-- ── Quick access ───────────────────────────────────────────────── -->
      <p class="section-label text-base-content/40 mt-2">Quick access</p>
      @for (link of quickLinks; track link.path) {
        <ui-button
          variant="ghost"
          [block]="true"
          extraClass="nav-row w-full justify-start !rounded-xl !border-none text-sm font-medium hover:!bg-[var(--border-color-contrast)]"
          (mousedown)="$event.preventDefault()"
          (buttonClick)="goTo(link.path)"
        >
          <ui-icon [name]="link.icon" [size]="20" class="shrink-0 text-base-content/40"></ui-icon>
          <span class="truncate">{{ link.label }}</span>
        </ui-button>
      }
    } @else if (results().length) {
      <!-- ── Results (backend relevance order, no categorisation) ──────── -->
      @for (result of results(); track $index) {
        <ng-container *ngTemplateOutlet="resultRow; context: { $implicit: result, rank: $index }" />
      }

      <ui-button
        variant="ghost"
        [block]="true"
        extraClass="gap-2 my-6 rounded-xl py-2.5 text-sm font-semibold normal-case text-primary hover:!border-primary/20 hover:!bg-primary/10"
        (mousedown)="$event.preventDefault(); discoverWith(query().trim())"
      >
        View all results for &ldquo;{{ query() }}&rdquo;
        <ui-icon name="arrow-right" [size]="14"></ui-icon>
      </ui-button>
    } @else if (showEmptyState()) {
      <div class="flex flex-col items-center gap-2 px-4 py-12 text-center">
        <ui-icon name="search" [size]="22" class="text-base-content/30"></ui-icon>
        <p class="text-sm font-semibold text-base-content/70">
          No results for &ldquo;{{ query() }}&rdquo;
        </p>
        <p class="text-xs text-base-content/40">Try a charity name, campaign or fundraiser title</p>
      </div>
    }
  </div>

  <!-- ── Shortcut hints ─────────────────────────────────────────────── -->
  <div
    class="flex shrink-0 items-center gap-4 border-t border-base-200 px-5 py-2.5 text-[11px] text-base-content/40"
  >
    <span class="flex items-center gap-1.5"><kbd class="kbd kbd-xs">↵</kbd> search</span>
    <span class="ml-auto flex items-center gap-1.5"><kbd class="kbd kbd-xs">esc</kbd> close</span>
  </div>
</div>

<!-- ── Reusable result row template ──────────────────────────────────── -->
<ng-template #resultRow let-result let-rank="rank">
  <ui-button
    variant="ghost"
    [block]="true"
    role="option"
    extraClass="h-auto min-h-0 w-full gap-3 rounded-xl px-3 py-2 text-left normal-case hover:!border-primary/20 hover:!bg-primary/10"
    (mousedown)="$event.preventDefault(); goToResult(result)"
  >
    <span
      class="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-base-200 ring-1 ring-base-300"
    >
      <ui-icon name="image" [size]="16" class="text-base-content/25"></ui-icon>
      @if (result.thumbnail) {
        <img
          [src]="result.thumbnail"
          [alt]="result.title"
          class="absolute inset-0 h-full w-full object-cover"
          (error)="$any($event.target).hidden = true"
        />
      }
    </span>
    <span class="min-w-0 flex-1">
      <span class="flex items-center gap-1.5">
        <span class="truncate text-sm font-semibold leading-snug text-base-content">
          {{ result.title }}
        </span>
        @if (rank === 0) {
          <ui-badge variant="primary" size="sm" [glow]="false" class="shrink-0">Top match</ui-badge>
        }
      </span>
      <span class="flex items-center gap-1 text-xs capitalize text-base-content/50">
        <!-- <ui-icon [name]="resultTypeIcons[result.type] || 'file'" [size]="12"></ui-icon> -->
        {{ result.type }}
      </span>
    </span>
  </ui-button>
</ng-template>
